/**
 * Ort-Typ-Chip eines Auftrags — reine Ableitung aus `events.locationType` + `events.offSiteMode`
 * für den Kopf des Auftrags-Dashboards (`event-detail.tsx`) UND die Ort-Spalte der
 * Veranstaltungsliste (`events.tsx`, offener Punkt P3 aus
 * `docs/KONZEPT-BETREIBER-UEBERSICHT-2026-09-04.md`) — EINE Stelle statt zwei Textbausteine, die
 * auseinanderlaufen können.
 *
 * KORREKTUR (Antoine, Chat 07.09.2026, Plan „Phase C.1 — Auftrags-Dashboard"): die drei echten
 * Kategorien sind „Im Haus · Event", „Außer Haus · Catering", „Abholung" — eine vierte Kategorie
 * „Lieferung" gibt es NICHT. Die dritte Kategorie ist inhaltlich neu gefasst (der Gast holt ab,
 * statt dass geliefert wird), nicht nur umbenannt.
 *
 * Der zugrundeliegende Datenbank-Wert bleibt bewusst `off_site_mode = 'delivery'` (siehe
 * `packages/db/sql/361_off_site_modus.sql` + Drizzle-Spiegel `packages/db/src/schema.ts`,
 * CHECK-Constraint `events_off_site_mode_check`): eine Verifikation gegen die Produktions-DB
 * (07.09.2026, Neon-Projekt `soft-lake-86506456`) ergab 0 Zeilen mit `off_site_mode IN
 * ('delivery','catering')` — das Feld wird bislang von niemandem gepflegt (49 `in_house`, 140
 * `NULL`, 0 `off_site`). Eine Umbenennung des Constraint-Werts selbst (`'delivery'` → z. B.
 * `'pickup'`) berührt Schreibpfade (`routes/events.ts`, Wetter-Logik, Agent-Werkzeuge), die
 * NICHT Teil von WP2 sind (die editierbare Auswahl kommt erst mit WP3a) — deshalb hier bewusst
 * NUR die Anzeige korrigiert, der Code-Wert unverändert gelassen. Siehe Rückmeldung des
 * WP2-Auftrags für die vollständige Herleitung.
 *
 * "Fehlt" heißt: `locationType` ist gar nicht gesetzt, ODER `locationType === 'off_site'` aber
 * `offSiteMode` ist (noch) nicht gesetzt — in beiden Fällen lässt sich keine der drei echten
 * Kategorien anzeigen, und es wird NICHT geraten, welche gemeint sein könnte.
 */
import type { PillTone } from "../ui/format";

export interface OrtTypChip {
  label: string;
  tone: PillTone;
  /** true, wenn der Ort-Typ (noch) nicht vollständig gepflegt ist — steuert den "Setzen"-Link. */
  fehlt: boolean;
}

const ORT_TYP_FEHLT: OrtTypChip = { label: "Ort-Typ fehlt", tone: "warn", fehlt: true };

export function ortTypChip(
  locationType: "in_house" | "off_site" | null | undefined,
  offSiteMode: "delivery" | "catering" | null | undefined,
): OrtTypChip {
  if (locationType === "in_house") return { label: "Im Haus · Event", tone: "neutral", fehlt: false };
  if (locationType === "off_site") {
    if (offSiteMode === "catering") return { label: "Außer Haus · Catering", tone: "neutral", fehlt: false };
    if (offSiteMode === "delivery") return { label: "Abholung", tone: "neutral", fehlt: false };
    return ORT_TYP_FEHLT;
  }
  return ORT_TYP_FEHLT;
}

/**
 * Fehlt der freie Ort-Text (`events.location`) wirklich etwas? EINE Stelle für den Seitenkopf
 * (`event-detail.tsx`) UND die Buchungen-Liste (`buchungen.tsx`) — Antoine, 13.09.2026, Live-Fund:
 * beide zeigten unabhängig voneinander bei „Im Haus" trotzdem eine „ergänzen"/„noch offen"-
 * Aufforderung an, obwohl die Adresse für „Im Haus" bereits über `tenant_settings.company_address`
 * feststeht (`apps/api/src/routes/weather.ts`, Kopfkommentar + Zeilen 127-139) — der Freitext ist
 * dort rein beschreibend (z. B. „Terrasse"), kein fehlender Pflichtwert. Bei „Außer Haus"/noch
 * unbekanntem Typ bleibt der Hinweis bestehen, weil dort tatsächlich noch eine Adresse fehlt.
 * „Konsistent überall" (Antoine) heißt: jede Anzeigestelle ruft DIESE Funktion statt eine eigene
 * `location ?? "…"`-Fallunterscheidung zu erfinden.
 */
export function ortFehltHinweis(
  location: string | null | undefined,
  locationType: "in_house" | "off_site" | null | undefined,
): boolean {
  if (location) return false;
  return locationType !== "in_house";
}
